import type {
    EcsConfigStateSnapshot,
    LogDatapointObservation,
    LogDatapointObserver,
    LogDatapointOutcome,
    PendingLogDatapointOutcome,
    LogFunctionName,
    WorkerLoggingFunctionArgs,
} from 'owa-analytics-types';
import { LOG_DATAPOINT_OBSERVATION_TIMEOUT } from 'owa-analytics-types';
import { trace } from 'owa-trace';

interface ObservationState {
    observer: LogDatapointObserver;
    complete: (outcome: LogDatapointOutcome) => void;
    cleanups: (() => void)[];
}

let nextObservationId = 0;
const observations = new Map<number, ObservationState>();
const remoteObservationsSeen = new Map<number, number>();

export function createLogDatapointObservation(
    mapping: EcsConfigStateSnapshot,
    createdAt: number,
    now: () => number
): PendingLogDatapointOutcome {
    const observation: LogDatapointObservation = {
        id: ++nextObservationId,
        createdAt,
        deadline: createdAt + LOG_DATAPOINT_OBSERVATION_TIMEOUT,
        mapping: { ...mapping },
    };
    const result = new Promise<LogDatapointOutcome>(complete => {
        addObservation(observation, complete, now);
    });
    return {
        observation,
        result,
        cancel: (outcome = 'cancelled') => settleLogDatapointObservation(observation.id, outcome),
    };
}

export function registerRemoteLogDatapointObservation(
    observation: LogDatapointObservation,
    complete: (outcome: LogDatapointOutcome) => void,
    now: () => number
): boolean {
    const currentTime = now();
    for (const [id, deadline] of remoteObservationsSeen) {
        if (deadline <= currentTime) {
            remoteObservationsSeen.delete(id);
        }
    }
    if (remoteObservationsSeen.has(observation.id)) {
        return false;
    }
    remoteObservationsSeen.set(observation.id, observation.deadline);
    addObservation(observation, complete, now);
    return getLogDatapointObserver(observation)?.isActive() === true;
}

export function cancelRemoteLogDatapointObservation(observation: LogDatapointObservation): void {
    if (!observations.has(observation.id)) {
        remoteObservationsSeen.set(observation.id, observation.deadline);
    } else {
        getLogDatapointObserver(observation)?.settle('cancelled');
    }
}

function addObservation(
    observation: LogDatapointObservation,
    complete: (outcome: LogDatapointOutcome) => void,
    now: () => number
): void {
    let dispatchStarted = false;
    const observer: LogDatapointObserver = {
        observation,
        isActive: () => {
            if (observations.get(observation.id)?.observer !== observer) {
                return false;
            }
            const currentTime = now();
            if (currentTime >= observation.deadline || currentTime < observation.createdAt) {
                settleLogDatapointObservation(observation.id, 'timeout');
                return false;
            }
            return true;
        },
        claimDispatch: () => {
            if (!observer.isActive() || dispatchStarted) {
                return false;
            }
            dispatchStarted = true;
            return true;
        },
        settle: outcome => {
            if (observer.isActive()) {
                settleLogDatapointObservation(observation.id, outcome);
            }
        },
        addCleanup: cleanup => {
            const state = observations.get(observation.id);
            if (state?.observer === observer) {
                state.cleanups.push(cleanup);
            } else {
                cleanup();
            }
        },
    };
    observations.set(observation.id, { observer, complete, cleanups: [] });
}

export function getLogDatapointObserver(
    observation: LogDatapointObservation
): LogDatapointObserver | undefined {
    const observer = observations.get(observation.id)?.observer;
    const expected = observer?.observation;
    if (
        expected?.createdAt === observation.createdAt &&
        expected.deadline === observation.deadline &&
        expected.mapping.accountKey === observation.mapping.accountKey &&
        expected.mapping.lifetimeId === observation.mapping.lifetimeId &&
        expected.mapping.etag === observation.mapping.etag &&
        expected.mapping.configIds === observation.mapping.configIds
    ) {
        return observer;
    }
    return undefined;
}

export function settleLogDatapointObservation(id: number, outcome: LogDatapointOutcome): void {
    const state = observations.get(id);
    if (!state) {
        return;
    }
    observations.delete(id);
    for (const cleanup of state.cleanups) {
        try {
            cleanup();
        } catch {
            trace.warn('Config-state observation cleanup failed.', 'analytics');
        }
    }
    state.complete(outcome);
}

export function isLogDatapointOutcome(value: unknown): value is LogDatapointOutcome {
    return (
        value === 'sdk-reported-sent' ||
        value === 'discarded' ||
        value === 'suppressed' ||
        value === 'offline' ||
        value === 'failed' ||
        value === 'timeout' ||
        value === 'unsupported' ||
        value === 'cancelled'
    );
}

export function getObservationFromLoggingArgs(
    name: LogFunctionName,
    args: WorkerLoggingFunctionArgs
): LogDatapointObservation | undefined {
    const candidate = name === 'logDatapoint' ? args[2] : undefined;
    return isLogDatapointObservation(candidate) ? candidate : undefined;
}

export function isLogDatapointObservation(value: unknown): value is LogDatapointObservation {
    return (
        typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        typeof value.id === 'number' &&
        Number.isSafeInteger(value.id) &&
        value.id > 0 &&
        'createdAt' in value &&
        typeof value.createdAt === 'number' &&
        Number.isFinite(value.createdAt) &&
        'deadline' in value &&
        typeof value.deadline === 'number' &&
        value.deadline === value.createdAt + LOG_DATAPOINT_OBSERVATION_TIMEOUT &&
        'mapping' in value &&
        typeof value.mapping === 'object' &&
        value.mapping !== null &&
        'accountKey' in value.mapping &&
        typeof value.mapping.accountKey === 'string' &&
        value.mapping.accountKey.length > 0 &&
        'lifetimeId' in value.mapping &&
        typeof value.mapping.lifetimeId === 'number' &&
        Number.isSafeInteger(value.mapping.lifetimeId) &&
        value.mapping.lifetimeId > 0 &&
        'etag' in value.mapping &&
        typeof value.mapping.etag === 'string' &&
        value.mapping.etag.length > 0 &&
        'configIds' in value.mapping &&
        typeof value.mapping.configIds === 'string' &&
        value.mapping.configIds.length > 0
    );
}
